import { useEffect, useRef, useCallback } from 'react';

interface UseInactivityProps {
  timeout: number;
  handleInactive: () => void;
  enableInactivityCheck: boolean;
}

const useInactivity = ({ timeout, handleInactive, enableInactivityCheck }: UseInactivityProps) => {
  const timer = useRef<number | null>(null);

  const resetTimer = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = window.setTimeout(handleInactive, timeout);
  }, [handleInactive, timeout]);

  const addEventListeners = useCallback((handler: () => void) => {
    window.addEventListener('mousemove', handler);
    window.addEventListener('keypress', handler);
    window.addEventListener('scroll', handler);
    window.addEventListener('click', handler);
  }, []);

  const removeEventListeners = useCallback((handler: () => void) => {
    window.removeEventListener('mousemove', handler);
    window.removeEventListener('keypress', handler);
    window.removeEventListener('scroll', handler);
    window.removeEventListener('click', handler);
  }, []);

  useEffect(() => {
    const eventHandler = () => resetTimer();

    if (enableInactivityCheck) {
      addEventListeners(eventHandler);
      resetTimer();
    } else {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      removeEventListeners(eventHandler);
    }

    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      removeEventListeners(eventHandler);
    };
  }, [enableInactivityCheck, resetTimer, addEventListeners, removeEventListeners]);

  return null;
};

export default useInactivity;
